import { useEffect, useRef, useState } from "react";

import AccountMenu from "@/components/account/AccountMenu";
import { getMapChromeTheme } from "@/components/mapChromeTheme";
import OnlineUsers from "@/components/OnlineUsers";
import { Button } from "@/components/ui/Button";
import type { RealtimeUser } from "@/hooks/useCampaignRealtimeChannel";
import type { AppProfile } from "@/lib/appProfile";
import { cn } from "@/lib/classNames";
import { useEditorStore } from "@/state/useEditorStore";
import * as Sentry from "@sentry/react";

const SAVE_SIGN_IN_TOOLTIP_TITLE = "Unlock your campaign tools";
const SAVE_SIGN_IN_TOOLTIP_DESCRIPTION =
  "Sign in to save to the cloud and keep building your world from anywhere.";
const SAVE_SIGN_IN_TOOLTIP_FEATURES = [
  {
    title: "Cloud save",
    description: "Save your campaign to the cloud.",
  },
  {
    title: "GM & Player Notes",
    description: "Create quests, lore or any notes either directly on the map or in a Notion-styled library.",
  },
  {
    title: "Shared campaign play",
    description: "Invite other players and Game Masters into your world.",
  },
] as const;

interface Props {
  onUndo: () => void;
  onRedo: () => void;
  onOpenSignIn: () => void;
  onStartFresh?: () => void;
  onOpenInvite?: () => void;
  onOpenCampaigns?: () => void;
  onOpenMembers?: () => void;
  onOpenKeyboardShortcuts?: () => void;
  canUndo: boolean;
  canRedo: boolean;
  cloudSignedIn: boolean;
  cloudSaving: boolean;
  hasActiveCampaign: boolean;
  campaignShortId?: string | null;
  onlineUsers?: RealtimeUser[];
  currentUserId?: string | null;
  /** True when the current user is owner|gm of the active campaign. */
  canToggleViewAs?: boolean;
  /** True when an admin is currently previewing as a player. */
  isPreviewingAsPlayer?: boolean;
  /** True when the current user can manage join codes / invites. */
  canInvite?: boolean;
  /** Profile for the signed-in user; required to render the account menu. */
  accountProfile?: Pick<AppProfile, "display_name" | "email"> | null;
  /** Toggles the campaign notebook modal. */
  onToggleNotebook?: () => void;
  /** True when the campaign notebook modal is open. */
  notebookOpen?: boolean;
  /**
   * True when the user has never opened the notebook before. When true, the
   * notebook icon plays a subtle highlight + ring pulse to draw attention to
   * the new home of the notebook.
   */
  notebookHighlightFirstTime?: boolean;
}

function UndoIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4 fill-none stroke-current"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M9 14 4 9l5-5" />
      <path d="M4 9h10a6 6 0 0 1 0 12h-3" />
    </svg>
  );
}

function RedoIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4 fill-none stroke-current"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="m15 14 5-5-5-5" />
      <path d="M20 9H10a6 6 0 0 0 0 12h3" />
    </svg>
  );
}

function HamburgerIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4 fill-none stroke-current"
      strokeWidth="2.25"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M4 7h16" />
      <path d="M4 12h16" />
      <path d="M4 17h16" />
    </svg>
  );
}

function CloseIcon({ className = "h-3.5 w-3.5" }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={`${className} fill-none stroke-current`}
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M6 6l12 12" />
      <path d="M18 6 6 18" />
    </svg>
  );
}

function SparkleIcon({ className = "h-3.5 w-3.5" }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={className}
      fill="currentColor"
    >
      <path d="M12 2.5 14.35 9.65 21.5 12l-7.15 2.35L12 21.5l-2.35-7.15L2.5 12l7.15-2.35L12 2.5Z" />
    </svg>
  );
}

function NotebookIcon({ className = "h-4 w-4" }: { className?: string }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 18 18"
      className={`${className} fill-none stroke-current`}
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M3.5 2.5h9a1.5 1.5 0 0 1 1.5 1.5v10a1.5 1.5 0 0 1-1.5 1.5h-9a1 1 0 0 1-1-1V3.5a1 1 0 0 1 1-1Z" />
      <path d="M2.5 6h11M2.5 9h11M2.5 12h7" />
    </svg>
  );
}

function NotebookToolbarButton({
  onClick,
  open,
  highlightFirstTime,
}: {
  onClick: () => void;
  open: boolean;
  highlightFirstTime: boolean;
}) {
  const tooltipLabel = open ? "Close notebook (N)" : "Open notebook (N)";
  return (
    <span className="relative inline-flex">
      <button
        type="button"
        onClick={onClick}
        aria-label={tooltipLabel}
        aria-pressed={open}
        title={tooltipLabel}
        data-testid="notebook-toolbar-button"
        className={cn(
          "relative inline-flex h-8 w-8 items-center justify-center border-2 border-neutral-950 text-neutral-950 transition-colors",
          open ? "bg-[#fff2d9]" : "bg-[#fffaf1] hover:bg-[#fff2d9]",
          highlightFirstTime && !open && "animate-[pulse_1.6s_ease-in-out_infinite] bg-[#fff2d9]",
        )}
      >
        <NotebookIcon />
        {highlightFirstTime && !open ? (
          <span
            aria-hidden="true"
            className="pointer-events-none absolute -inset-1 animate-ping rounded-sm border-2 border-[#9c4c32]/70"
          />
        ) : null}
      </button>
    </span>
  );
}

function HistoryIconButton({
  onClick,
  disabled,
  label,
  children,
}: {
  onClick: () => void;
  disabled: boolean;
  label: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      title={label}
      className="inline-flex h-8 w-8 items-center justify-center border-2 border-neutral-950 bg-[#fffaf1] text-neutral-950 transition-colors hover:bg-[#fff2d9] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:bg-[#fffaf1]"
    >
      {children}
    </button>
  );
}

function SaveStatusIndicator({
  saving,
  signedIn,
}: {
  saving: boolean;
  signedIn: boolean;
}) {
  const theme = getMapChromeTheme();

  if (!signedIn) {
    return (
      <span className="group relative inline-flex">
        <span
          className="inline-flex cursor-help select-none items-center rounded-full border-2 border-neutral-950 bg-[#fff2d9] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.12em] text-neutral-950 transition-colors group-hover:bg-[#ffe7bc]"
          aria-label="Save status"
          data-testid="save-status"
        >
          Sign in to get full experience
        </span>
        <span
          className={`pointer-events-none absolute left-1/2 top-full z-10 mt-2 flex w-[24rem] -translate-x-1/2 flex-col gap-3 px-4 py-4 text-left opacity-0 shadow-[10px_10px_0_rgba(19,19,19,0.12)] transition-opacity duration-150 group-hover:opacity-100 ${theme.tooltip}`}
          role="tooltip"
        >
          <span className="inline-flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#9c4c32]">
            <SparkleIcon className="h-3 w-3 text-[#d18a52]" />
            Cloud features
          </span>
          <span className="text-[17px] font-semibold leading-tight text-neutral-950">
            {SAVE_SIGN_IN_TOOLTIP_TITLE}
          </span>
          <span className="text-sm leading-snug text-neutral-950/72">
            {SAVE_SIGN_IN_TOOLTIP_DESCRIPTION}
          </span>
          <span className="grid gap-2">
            {SAVE_SIGN_IN_TOOLTIP_FEATURES.map((feature) => (
              <span
                key={feature.title}
                className="flex flex-col gap-1 rounded-lg border border-neutral-950/12 bg-[#fff2d9]/55 px-3 py-2.5"
              >
                <span className="inline-flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#3e664d]">
                  <SparkleIcon className="h-3 w-3 text-[#d18a52]" />
                  {feature.title}
                </span>
                <span className="text-sm leading-snug text-neutral-950/80">
                  {feature.description}
                </span>
              </span>
            ))}
          </span>
        </span>
      </span>
    );
  }

  if (saving) {
    return (
      <span
        className="inline-flex select-none items-center gap-2 rounded-full border-2 border-[#2d5a3d] bg-[#e4f4e8] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1f5131] shadow-sm"
        aria-live="polite"
        aria-label="Saving"
        data-testid="save-status"
      >
        <span
          className="inline-block h-2 w-2 animate-pulse rounded-full bg-amber-500"
          aria-hidden
        />
        Saving…
      </span>
    );
  }

  return (
    <span
      className="inline-flex select-none items-center gap-2 rounded-full border-2 border-[#2d5a3d] bg-[#e4f4e8] px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#1f5131] shadow-sm"
      aria-live="polite"
      aria-label="All changes saved"
      data-testid="save-status"
    >
      <span
        className="inline-block h-2 w-2 rounded-full bg-emerald-600"
        aria-hidden
      />
      Saved
    </span>
  );
}

function ToolbarMenu({
  showCampaigns,
  onOpenCampaigns,
  showMembers,
  onOpenMembers,
}: {
  showCampaigns: boolean;
  onOpenCampaigns?: () => void;
  showMembers: boolean;
  onOpenMembers?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const theme = getMapChromeTheme();

  useEffect(() => {
    if (!open) {
      return;
    }

    function handlePointerDown(event: PointerEvent) {
      if (!menuRef.current?.contains(event.target as Node)) {
        setOpen(false);
      }
    }

    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === "Escape") {
        setOpen(false);
      }
    }

    document.addEventListener("pointerdown", handlePointerDown);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("pointerdown", handlePointerDown);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open]);

  if (!showCampaigns && !showMembers) {
    return null;
  }

  return (
    <div ref={menuRef} className="relative shrink-0">
      <button
        type="button"
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Open campaign menu"
        data-testid="toolbar-menu-trigger"
        onClick={() => setOpen((current) => !current)}
        className="inline-flex h-8 w-8 items-center justify-center border-2 border-neutral-950 bg-[#fffaf1] text-neutral-950 transition-colors hover:bg-[#fff2d9]"
      >
        <HamburgerIcon />
      </button>

      <div
        role="menu"
        aria-label="Campaign menu"
        hidden={!open}
        className={`absolute left-0 top-[calc(100%+0.5rem)] z-30 flex min-w-52 w-max flex-col gap-2 p-2 shadow-[10px_10px_0_rgba(19,19,19,0.12)] ${theme.tooltip}`}
      >
        {showCampaigns ? (
          <Button
            type="button"
            role="menuitem"
            variant="secondary"
            chrome="compact"
            className="w-full justify-start whitespace-nowrap text-left"
            data-testid="toolbar-menu-campaigns"
            onClick={() => {
              setOpen(false);
              onOpenCampaigns?.();
            }}
          >
            Campaigns
          </Button>
        ) : null}

        {showMembers ? (
          <Button
            type="button"
            role="menuitem"
            variant="secondary"
            chrome="compact"
            className="w-full justify-start whitespace-nowrap text-left"
            data-testid="toolbar-menu-members"
            onClick={() => {
              setOpen(false);
              onOpenMembers?.();
            }}
          >
            Members
          </Button>
        ) : null}
      </div>
    </div>
  );
}

export default function Toolbar({
  onUndo,
  onRedo,
  onOpenSignIn,
  onStartFresh,
  onOpenInvite,
  onOpenCampaigns,
  onOpenMembers,
  onOpenKeyboardShortcuts,
  canUndo,
  canRedo,
  cloudSignedIn,
  cloudSaving,
  hasActiveCampaign,
  campaignShortId: _campaignShortId,
  onlineUsers,
  currentUserId,
  canToggleViewAs = false,
  isPreviewingAsPlayer = false,
  canInvite = false,
  accountProfile = null,
  onToggleNotebook,
  notebookOpen = false,
  notebookHighlightFirstTime = false,
}: Props) {
  const theme = getMapChromeTheme();
  const setViewAs = useEditorStore((s) => s.setViewAs);
  const activeCampaignId = useEditorStore((s) => s.activeCampaignId);

  const handleToggleViewAs = () => {
    const next = isPreviewingAsPlayer ? "actual" : "player";
    setViewAs(next);
    Sentry.logger.info("view_as.toggle", {
      from: isPreviewingAsPlayer ? "player" : "actual",
      to: next,
      campaignId: activeCampaignId ?? "(none)",
    });
  };

  const otherOnlineUsers = (onlineUsers ?? []).filter(
    (u) => u.userId !== currentUserId,
  );

  return (
    <header className={`fixed top-0 left-0 right-0 z-20 ${theme.toolbarShell}`}>
      <div className="flex flex-wrap items-stretch">
        {/* Tool buttons */}
        <div className="flex flex-wrap items-center gap-2 px-4 py-2.5">
          <ToolbarMenu
            showCampaigns={cloudSignedIn && Boolean(onOpenCampaigns)}
            onOpenCampaigns={onOpenCampaigns}
            showMembers={cloudSignedIn && hasActiveCampaign && Boolean(onOpenMembers)}
            onOpenMembers={onOpenMembers}
          />

          <div className="flex items-center gap-1">
            <HistoryIconButton onClick={onUndo} disabled={!canUndo} label="Undo">
              <UndoIcon />
            </HistoryIconButton>
            <HistoryIconButton onClick={onRedo} disabled={!canRedo} label="Redo">
              <RedoIcon />
            </HistoryIconButton>
          </div>

          {onOpenKeyboardShortcuts ? (
            <Button
              type="button"
              onClick={onOpenKeyboardShortcuts}
              variant="secondary"
              chrome="compact"
              data-testid="control-button"
            >
              Controls
            </Button>
          ) : null}

          {canToggleViewAs && hasActiveCampaign ? (
            isPreviewingAsPlayer ? (
              <button
                type="button"
                className="inline-flex items-center gap-1.5 border-[3px] border-[#9c4c32] bg-[#ffe7bc] px-2 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#5d2f26] transition-colors hover:bg-[#ffdba0]"
                aria-label="Stop viewing as player"
                data-testid="view-as-player-toggle"
                title="Stop viewing as player"
                onClick={handleToggleViewAs}
              >
                <CloseIcon className="h-3 w-3" />
                Viewing as player
              </button>
            ) : (
              <Button
                type="button"
                onClick={handleToggleViewAs}
                variant="secondary"
                chrome="compact"
                className="whitespace-nowrap"
                aria-label="View as player"
                title="Preview the campaign as a player would see it"
                data-testid="view-as-player-toggle"
              >
                View as player
              </Button>
            )
          ) : null}

          {!cloudSignedIn && onStartFresh ? (
            <Button
              onClick={onStartFresh}
              variant="secondary"
              chrome="compact"
              type="button"
            >
              Start fresh
            </Button>
          ) : null}

          {canInvite && hasActiveCampaign && onOpenInvite ? (
            <Button
              type="button"
              onClick={onOpenInvite}
              variant="secondary"
              chrome="compact"
              data-testid="invite-button"
            >
              Invite
            </Button>
          ) : null}

          {(hasActiveCampaign || !cloudSignedIn) && onToggleNotebook ? (
            <NotebookToolbarButton
              onClick={onToggleNotebook}
              open={notebookOpen}
              highlightFirstTime={notebookHighlightFirstTime}
            />
          ) : null}

          {!cloudSignedIn ? (
            <Button onClick={onOpenSignIn} variant="primary" chrome="compact" type="button">
              Sign In
            </Button>
          ) : null}

          {!cloudSignedIn || hasActiveCampaign ? (
            <SaveStatusIndicator saving={cloudSaving} signedIn={cloudSignedIn} />
          ) : null}
        </div>

        {/* Right gutter: presence + account */}
        <div className="ml-auto flex items-center gap-3 px-4 py-2.5">
          {otherOnlineUsers.length > 0 && (
            <OnlineUsers users={onlineUsers ?? []} currentUserId={currentUserId ?? undefined} />
          )}
          {cloudSignedIn && accountProfile ? (
            <AccountMenu profile={accountProfile} />
          ) : null}
        </div>
      </div>
    </header>
  );
}
